import React, { useRef, useState } from 'react';
import { compressAvatarFile, initialsFromName } from './profileUtils.js';

export default function IdentityEditor({
    name,
    avatarUrl,
    onCancel,
    onSave,
}) {
    const [draftName, setDraftName] = useState(name || '');
    const [draftAvatar, setDraftAvatar] = useState(avatarUrl || null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const fileRef = useRef(null);

    const initials = initialsFromName(draftName || name);

    const handlePhoto = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
            setError(null);
            const data = await compressAvatarFile(file);
            setDraftAvatar(data);
        } catch (err) {
            setError(err.message || 'Could not use that photo');
        }
    };

    const handleSave = async () => {
        const nextName = draftName.replace(/\s+/g, ' ').trim();
        if (!nextName) {
            setError('Enter a display name');
            return;
        }
        setSaving(true);
        setError(null);
        let timer;
        try {
            await Promise.race([
                onSave({ displayName: nextName, avatarUrl: draftAvatar }),
                new Promise((_, reject) => {
                    timer = setTimeout(() => reject(new Error('That took too long. Refresh and try again.')), 12000);
                }),
            ]);
        } catch (err) {
            setError(err.message || 'Could not save profile');
        } finally {
            clearTimeout(timer);
            setSaving(false);
        }
    };

    return (
        <div className="profile-sheet" role="dialog" aria-labelledby="identity-editor-title">
            <h3 id="identity-editor-title">Edit profile</h3>
            <div className="profile-identity-edit">
                <button
                    type="button"
                    className={`profile-avatar profile-avatar--edit${draftAvatar ? ' profile-avatar--photo' : ''}`}
                    style={draftAvatar ? { backgroundImage: `url("${draftAvatar}")` } : undefined}
                    onClick={() => fileRef.current?.click()}
                    aria-label="Change photo"
                >
                    {draftAvatar ? null : <span>{initials}</span>}
                </button>
                <div className="profile-identity-edit-actions">
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        hidden
                        onChange={handlePhoto}
                    />
                    <button type="button" className="profile-chip-btn" onClick={() => fileRef.current?.click()}>
                        Photo
                    </button>
                    <button
                        type="button"
                        className={`profile-chip-btn${draftAvatar ? '' : ' is-active'}`}
                        onClick={() => setDraftAvatar(null)}
                    >
                        Initials
                    </button>
                </div>
            </div>
            <label className="profile-field">
                <span>Display name</span>
                <input
                    type="text"
                    value={draftName}
                    maxLength={80}
                    onChange={(e) => setDraftName(e.target.value)}
                    autoComplete="nickname"
                />
            </label>
            {error ? <p className="profile-error">{error}</p> : null}
            <div className="profile-sheet-actions">
                <button type="button" className="profile-btn" onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
                <button type="button" className="profile-btn profile-btn--primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    );
}
